// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders / Re-order  (route /orders/:ref/reorder)
// Review before you re-order. Every line of a past order with a tick box, quantity stepper, the pack · cut, what one pack
// cost THEN vs TODAY (price changes are flagged, never hidden), and whether it can still be ordered: a catch that is no
// longer on the menu is greyed out; a cut that is no longer offered is replaced by the catch's default cut and says so;
// a catch that is in today's live Daily Catch shows how many packs are left (sold out is flagged). Totals compare what
// was paid last time with today's price for the selection. "Add to cart" (optionally replacing the current cart) or
// "Add & go to checkout". Prices always come from the menu (utils/reorder.ts), so the cart and checkout agree.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, TriangleAlert } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { MAX_PACKS } from "../hooks/useCatchDraft";
import useAuth from "../hooks/useAuth";
import useCart from "../hooks/useCart";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { fetchDrop, type Landing } from "../utils/drop";
import { fetchOrder, formatWhen, type Order } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";
import { reorderEntries } from "../utils/reorder";

type Load = { status: "loading" } | { status: "missing" } | { status: "error" } | { status: "ready"; order: Order };

export default function OrderReorder() {
  const { ref = "" } = useParams();
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [landings, setLandings] = useState<Landing[]>([]);
  useDocumentTitle("Re-order");

  useEffect(() => {
    if (!signedIn) return;
    fetchOrder(ref).then(
      (order) => setLoad({ status: "ready", order }),
      (error) => setLoad({ status: error instanceof ApiError && error.status === 404 ? "missing" : "error" }),
    );
    fetchDrop().then((drop) => setLandings(drop.landings), () => undefined); // stock notes are a bonus, never a blocker
  }, [signedIn, ref]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  if (load.status !== "ready") {
    return (
      <PageTransition>
        <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
          <div className="mx-auto max-w-xl px-4">
            <p role={load.status === "loading" ? "status" : "alert"} className="rounded-2xl bg-white p-8 text-center text-sm shadow-sm dark:bg-ocean-900">
              {load.status === "loading" ? "Loading your order…" : load.status === "missing" ? "We couldn't find that order." : "We couldn't load this order. Check your connection and try again."}{" "}
              <Link to="/orders" className="font-semibold underline">My orders</Link>
            </p>
          </div>
        </div>
      </PageTransition>
    );
  }
  return <ReorderForm order={load.order} landings={landings} />;
}

function ReorderForm({ order, landings }: { order: Order; landings: Landing[] }) {
  const navigate = useNavigate();
  const { lines, setQty } = useCart();
  const entries = useMemo(() => reorderEntries(order), [order]);
  const [picks, setPicks] = useState(() => entries.map((entry) => ({ on: entry.item !== null, qty: entry.line.qty })));
  const [replace, setReplace] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const chosen = entries.map((entry, i) => ({ entry, pick: picks[i] })).filter(({ entry, pick }) => entry.item && pick.on && pick.qty > 0);
  const todayTotal = chosen.reduce((sum, { entry, pick }) => sum + entry.todayUnit * pick.qty, 0);
  const paidTotal = chosen.reduce((sum, { entry, pick }) => sum + entry.paidUnit * pick.qty, 0);
  const diff = todayTotal - paidTotal;

  const update = (i: number, change: Partial<(typeof picks)[number]>) => setPicks((current) => current.map((pick, index) => (index === i ? { ...pick, ...change } : pick)));

  const apply = () => {
    if (replace) for (const line of lines) setQty(line.item.id, line.pack.id, 0);
    for (const { entry, pick } of chosen) {
      if (!entry.item || !entry.cut) continue;
      const existing = replace ? 0 : (lines.find((line) => line.item.id === entry.item?.id && line.pack.id === entry.pack.id)?.qty ?? 0);
      setQty(entry.item.id, entry.pack.id, Math.min(MAX_PACKS, existing + pick.qty), entry.cut.id);
    }
  };
  const addOnly = () => {
    apply();
    setDone(`${chosen.length} ${chosen.length === 1 ? "item" : "items"} added to your cart at today's prices.`);
  };
  const addAndCheckout = () => {
    apply();
    navigate("/checkout");
  };

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-44 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs dynamicLabel={order.ref} />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to={`/orders/${order.ref}`} aria-label="Back to the order" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div>
              <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">Re-order</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">From <span className="font-mono">{order.ref}</span> · {formatWhen(order.createdAt)}</p>
            </div>
          </header>

          <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">Tick what you want again and adjust the quantity. Prices are today&apos;s menu prices.</p>

          <ul className="flex flex-col gap-3">
            {entries.map((entry, i) => {
              const pick = picks[i];
              const gone = entry.item === null;
              const landing = landings.find((candidate) => candidate.itemId === entry.line.id);
              const change = entry.todayUnit - entry.paidUnit;
              const id = `reorder-${i}`;
              return (
                <li key={`${entry.line.id}-${entry.line.pack}`} className={`rounded-2xl bg-white p-4 shadow-sm ring-1 dark:bg-ocean-900 ${pick.on && !gone ? "ring-2 ring-teal-600" : "ring-ocean-900/5 dark:ring-white/10"} ${gone ? "opacity-70" : ""}`}>
                  <div className="flex gap-3">
                    <input id={id} type="checkbox" checked={pick.on && !gone} disabled={gone} onChange={(e) => update(i, { on: e.target.checked })} className="mt-1 h-5 w-5 shrink-0 accent-teal-600" />
                    {entry.item ? <img src={entry.item.image} alt="" width={56} height={56} loading="lazy" className="h-14 w-14 shrink-0 rounded-xl object-cover" /> : <span aria-hidden="true" className="h-14 w-14 shrink-0 rounded-xl bg-ocean-700/10" />}
                    <div className="min-w-0 flex-1">
                      <label htmlFor={id} className="block font-semibold leading-snug text-ocean-950 dark:text-foam-50">{entry.line.name}</label>
                      <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{entry.pack.label} · {entry.cut?.name ?? entry.line.cut}</p>
                      {gone ? (
                        <p className="mt-1 text-xs font-semibold text-coral-500 dark:text-coral-300">No longer on the menu</p>
                      ) : (
                        <p className="mt-1 text-xs text-ocean-800/85 dark:text-foam-100/80">
                          Paid {formatInr(entry.paidUnit)} · today <strong className="text-ocean-950 dark:text-foam-50">{formatInr(entry.todayUnit)}</strong> each
                          {change !== 0 && (
                            <span className={`ml-1 font-bold ${change > 0 ? "text-coral-500 dark:text-coral-300" : "text-teal-700 dark:text-teal-300"}`}>
                              ({change > 0 ? "up" : "down"} {formatInr(Math.abs(change))})
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  {!gone && entry.cutChanged && (
                    <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-coral-400/10 px-3 py-2 text-xs font-semibold text-coral-500 dark:text-coral-300">
                      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      “{entry.line.cut}” isn&apos;t offered any more — we&apos;ll use {entry.cut?.name} instead.
                    </p>
                  )}
                  {!gone && landing && (
                    <p className="mt-2 text-xs font-semibold text-ocean-800/85 dark:text-foam-100/80">
                      In today&apos;s Daily Catch: {landing.soldOut ? "sold out right now" : `${landing.packsLeft} ${landing.packsLeft === 1 ? "pack" : "packs"} left`}. <Link to="/daily-catch" className="underline">See Daily Catch</Link>
                    </p>
                  )}

                  {!gone && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3" role="group" aria-label={`Quantity of ${entry.line.name}`}>
                        <button type="button" onClick={() => update(i, { qty: Math.max(1, pick.qty - 1) })} disabled={pick.qty <= 1} aria-label={`Fewer ${entry.line.name}`} className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-700/10 text-ocean-900 hover:bg-ocean-700/15 disabled:opacity-40 dark:bg-white/10 dark:text-foam-50">
                          <Minus className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <span aria-live="polite" className="w-6 text-center font-bold tabular-nums text-ocean-950 dark:text-foam-50">{pick.qty}</span>
                        <button type="button" onClick={() => update(i, { qty: Math.min(MAX_PACKS, pick.qty + 1) })} disabled={pick.qty >= MAX_PACKS} aria-label={`More ${entry.line.name}`} className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-800 text-white hover:bg-ocean-700 disabled:opacity-40">
                          <Plus className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                      <p className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{formatInr(entry.todayUnit * pick.qty)}</p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <label className="flex items-start gap-3 rounded-2xl bg-white p-4 text-sm shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="mt-0.5 h-5 w-5 accent-teal-600" />
            <span>
              <span className="block font-semibold text-ocean-950 dark:text-foam-50">Replace what&apos;s already in my cart</span>
              <span className="text-ocean-800/80 dark:text-foam-100/75">{lines.length === 0 ? "Your cart is empty." : `You have ${lines.length} ${lines.length === 1 ? "line" : "lines"} in your cart; otherwise these are added to them.`}</span>
            </span>
          </label>

          {done && (
            <p role="status" className="rounded-xl bg-teal-100 px-4 py-2 text-sm font-semibold text-teal-700">
              {done} <Link to="/checkout" className="underline">Go to checkout</Link>
            </p>
          )}
        </div>

        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-ocean-900/10 bg-white px-4 pb-4 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-ocean-900">
          <div className="mx-auto max-w-xl pr-16 sm:pr-0">
            <div className="flex items-end justify-between gap-3">
              <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">
                {chosen.length} selected · today <span className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">{formatInr(todayTotal)}</span>
              </p>
              <p className="text-right text-xs text-ocean-800/80 dark:text-foam-100/75">
                Paid then {formatInr(paidTotal)}
                {diff !== 0 && <span className={`block font-bold ${diff > 0 ? "text-coral-500 dark:text-coral-300" : "text-teal-700 dark:text-teal-300"}`}>{diff > 0 ? "+" : "−"}{formatInr(Math.abs(diff))} vs last time</span>}
              </p>
            </div>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={addOnly} disabled={chosen.length === 0} className="min-h-12 flex-1 rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 hover:bg-ocean-700/15 disabled:opacity-40 dark:bg-white/10 dark:text-foam-50">
                Add to cart
              </button>
              <button type="button" onClick={addAndCheckout} disabled={chosen.length === 0} className="min-h-12 flex-1 rounded-full bg-ocean-800 px-4 text-sm font-bold text-white hover:bg-ocean-700 disabled:opacity-40">
                Add &amp; checkout
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
